import { pluralRo, type Count } from '../lib/format'

import type { Translations } from './types'

/**
 * Romanian. Typed against the Russian source: every key and every function
 * signature must match. Counts use `pluralRo`, which knows that twenty and
 * more take « de » (« 20 de abonați »). Amounts and dates arrive already
 * formatted; the dictionary supplies only the words around them.
 */
export const ro: Translations = {
  common: {
    all: 'Max',
    back: 'Înapoi',
    cancel: 'Anulează',
    confirm: 'Confirmă',
    copy: 'Copiază',
    copied: 'Copiat',
    open: 'Deschide',
    retry: 'Reîncearcă',
    save: 'Salvează',
    close: 'Închide',
    loading: 'Se încarcă',
    available: 'Disponibil',
    total: 'Total',
    amount: 'Sumă',
    network: 'Rețea',
    asset: 'Monedă',
    status: 'Stare',
    date: 'Dată',
    nothingYet: 'Nimic încă',
    somethingWentWrong: 'Ceva n-a mers bine',
    reload: 'Reîncarcă',
    crashHint: 'Ecranul nu a putut fi afișat. Reîncarcă aplicația: fondurile și cererile tale nu sunt afectate.',
  },

  nav: {
    label: 'Navigare principală',
    home: 'Acasă',
    markets: 'Piețe',
    signals: 'Semnale',
    trade: 'Trading',
    wallet: 'Active',
  },

  auth: {
    signIn: 'Autentificare',
    signInSubtitle: 'Intră ca să revii la semnalele și soldul tău',
    signInAction: 'Intră',
    signUp: 'Înregistrare',
    signUpSubtitle: 'E-mail și parolă: atât e nevoie ca să începi',
    signUpSubtitleInvite: 'E-mail, parolă și cod de invitație: atât e nevoie ca să începi',
    email: 'E-mail',
    emailPlaceholder: 'nume@exemplu.com',
    password: 'Parolă',
    passwordHint: 'Minimum 8 caractere, cu litere și cifre',
    passwordConfirm: 'Repetă parola',
    referralCode: 'Cod de invitație',
    referralCodePlaceholder: 'De exemplu, 8K3M7QPL',
    referralCodeHint: 'Opțional: dacă te-a invitat un membru 19CRT',
    referralCodeRequiredHint: 'Înregistrarea se face doar pe bază de invitație: folosește linkul sau codul celui care te-a invitat',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} ${pluralRo(signals, 'semnal bonus', 'semnale bonus')}` : null,
        promo === null ? null : `un bonus promoțional de ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Dacă ai primit codul de la un membru 19CRT, invitația contează după o primă depunere de cel puțin ${deposit} ${asset}`
        : `Dacă ai primit codul de la un membru 19CRT, după o primă depunere de cel puțin ${deposit} ${asset} primești ${perks.join(' și ')}`
    },
    forgot: 'Ai uitat parola?',
    createAccount: 'Creează cont',
    haveAccount: 'Ai deja cont?',
    noAccount: 'Nu ai cont?',
    backToSignIn: 'Înapoi la autentificare',
    verifyTitle: 'Confirmă e-mailul',
    verifySent: (email: string) => `Am trimis un e-mail la ${email}`,
    verifySentAnywhere: 'Am trimis un e-mail cu linkul la adresa ta',
    verifyHint:
      'Deschide linkul din e-mail ca să confirmi adresa: acolo ajung e-mailurile de recuperare a parolei. Dacă nu-l vezi, verifică dosarul „Spam”.',
    verifyEmailPrompt: 'Indică e-mailul cu care este înregistrat contul',
    verifyResend: 'Retrimite e-mailul',
    verifyResendIn: (seconds: Count) => `Poți retrimite peste ${seconds.text} s`,
    verifyResent: 'Am trimis încă un e-mail',
    verifyResentMaybe: 'Dacă adresa este înregistrată și încă neconfirmată, un nou e-mail este pe drum',
    verifyChecking: 'Verificăm linkul',
    verifyDone: 'E-mail confirmat',
    verifyDoneHint: 'Deschidem aplicația',
    verifyDoneSignInHint: 'Deschidem autentificarea',
    resetTitle: 'Recuperarea parolei',
    resetSubtitle: 'Indică e-mailul: îți trimitem un link pentru schimbarea parolei',
    resetSend: 'Trimite linkul',
    resetSent: 'Dacă acest cont există, e-mailul a fost deja trimis',
    resetSentHint:
      'Nu arătăm dacă o adresă este înregistrată: altfel, cu acest formular s-ar putea verifica e-mailurile altora. Uită-te în „Primite” și în „Spam”.',
    newPassword: 'Parolă nouă',
    newPasswordSubtitle: 'Alege o parolă nouă. Cea veche nu va mai funcționa imediat.',
    newPasswordSubmit: 'Salvează parola',
    requestNewLink: 'Cere un link nou',
    newPasswordDone: 'Parola a fost actualizată',
    newPasswordDoneHint: 'Deschidem autentificarea',
    signOut: 'Ieși',
    totpCode: 'Cod de confirmare',
    totpHint: 'Cele șase cifre din aplicația de autentificare',
    recoveryCode: 'Cod de recuperare',
    recoveryCodeHint: 'Opt caractere din codurile salvate la activarea 2FA',
    useRecoveryCode: 'Folosește un cod de recuperare',
    useAppCode: 'Folosește aplicația de autentificare',
    resetLinkInvalid: 'Linkul nu funcționează',
    errors: {
      invalidCredentials: 'E-mail sau parolă greșite',
      emailTaken: 'Acest e-mail este deja înregistrat',
      weakPassword: 'Parola este prea scurtă',
      passwordRule: 'Parola trebuie să conțină litere și cifre',
      passwordsDiffer: 'Parolele nu coincid',
      invalidEmail: 'Introdu o adresă în formatul nume@exemplu.com',
      invalidReferralCode: 'Verifică codul de invitație',
      referralRequired: 'Introdu codul de invitație',
      emailRequired: 'Indică e-mailul',
      passwordRequired: 'Introdu parola',
      accountBlocked: 'Accesul la cont este restricționat. Scrie la asistență',
      rateLimited: 'Prea multe încercări. Încearcă mai târziu',
      totpRequired: 'Introdu codul din aplicația de autentificare',
      totpInvalid: 'Codul nu a fost acceptat. Verifică ora telefonului și introdu unul nou',
      totpFormat: 'Codul din aplicație are șase cifre, un cod de recuperare are opt caractere',
      expiredToken: 'Linkul a expirat. Cere unul nou',
      verifyLinkUsed: 'Acest link a fost deja folosit. Autentifică-te sau deschide cel mai recent e-mail, dacă ai cerut unul nou',
      missingToken: 'Linkul este incomplet. Deschide-l întreg din e-mail',
      validation: 'Verifică câmpurile completate',
      network: 'Nu există conexiune cu serverul. Verifică rețeaua',
    },
  },

  home: {
    promoLabel: 'Promoții',
    banners: [
      {
        title: 'Semnale pe aur',
        subtitle: 'Un cod din canalul nostru de Telegram: o tranzacție dintr-un singur pas',
        cta: 'Introdu codul',
      },
      {
        title: 'Piețe în timp real',
        subtitle: 'Grafice și registru de ordine live pentru toate perechile',
        cta: 'Deschide piețele',
      },
      {
        title: 'Depune în câteva minute',
        subtitle: 'USDT în rețelele TRC20 și ERC20',
        cta: 'Alimentează contul',
      },
    ],
    pauseAnnouncements: 'Pune anunțurile pe pauză',
    resumeAnnouncements: 'Reia anunțurile',
    announcementsLabel: 'Anunțuri',
    announcements: [
      'Înainte de depunere, verifică moneda și rețeaua transferului',
      'Gestionează protecția contului în secțiunea „Securitate”',
      'Istoricul operațiunilor este în secțiunea „Active”',
    ],
    totalBalance: 'Sold total',
    quickTrade: 'Trading rapid',
    quickTradeHint: 'Spot și futures într-un singur terminal',
    quickActions: 'Acțiuni rapide',
    signalLive: 'Semnal disponibil',
    signalNext: 'Următorul semnal',
    signalClosesIn: 'se închide în',
    signalOpensAt: (time: string) => `se deschide la ${time}`,
    signalsTitle: 'Semnale pe aur',
    allSignals: 'Toate semnalele',
    marketMoves: 'Mișcarea pieței',
    pair: 'Pereche',
    lastPrice: 'Preț',
    change24h: 'Variație 24 h',
    /** After the figure in the home balance's change pill: « +1.30% în 24 h ». */
    change24hShort: 'în 24 h',
    volume24h: 'Volum 24 h',
    allMarkets: 'Toate piețele',
    pricesUnavailable: 'Cotațiile nu sunt disponibile: nu putem evalua întregul portofoliu',
    ratesUnavailable: 'Cotațiile sunt temporar indisponibile',
    marketsUnavailable: 'Lista piețelor este temporar indisponibilă',
    signalsNone: 'Nu există semnale acum: codurile noi apar în canalul nostru de Telegram',
  },

  markets: {
    title: 'Piețe',
    search: 'Caută o pereche',
    spot: 'Spot',
    futures: 'Futures',
    gainers: 'Creșteri',
    losers: 'Scăderi',
    volume: 'Volum 24 h',
    volumeShort: 'volum',
    typeLabel: 'Tip de piață',
    sortLabel: 'Sortare',
    clearSearch: 'Șterge căutarea',
    favourites: 'Favorite',
    addFavourite: 'Adaugă la favorite',
    removeFavourite: 'Scoate din favorite',
    emptyFavourites: 'Nu ai favorite încă: marchează perechi cu steluța',
    empty: 'Nu s-a găsit nimic',
    emptyHint: 'Verifică simbolul perechii, de exemplu BTC sau SOL.',
    emptySpot: 'Nu sunt încă perechi spot deschise',
    emptyFutures: 'Nu sunt încă perechi futures deschise',
    loadFailed: 'Lista piețelor nu a putut fi încărcată',
    pricesUnavailable: 'Cotațiile sunt temporar indisponibile. Prețurile se vor actualiza automat.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Aur' } as Record<string, string>,
  },

  wallet: {
    title: 'Active',
    totalValue: 'Valoarea totală a contului',
    accounts: 'Conturile mele',
    latestWithdrawal: 'Ultima retragere',
    coin: 'Monedă',
    searchCoin: 'Caută o monedă',
    quickActions: 'Acțiuni rapide',
    coins: 'Monede',
    hideBalance: 'Ascunde soldul',
    showBalance: 'Arată soldul',
    loadError: 'Soldul nu a putut fi încărcat',
    pricesUnavailable: 'Cotațiile nu sunt disponibile: întregul portofoliu nu poate fi evaluat',
    coinsEmpty: 'Nu ai monede încă. Alimentează contul și vor apărea aici.',
    accountSpot: 'Cont spot',
    accountSpotHint: 'Disponibil pentru tranzacționare',
    accountFutures: 'Cont futures',
    accountFuturesHint: 'Marja pozițiilor',
    accountCopy: 'Copy trading (arhivă)',
    accountCopyHint: 'Transferă-l în spot',
    actions: {
      deposit: 'Depune',
      withdraw: 'Retrage',
      transfer: 'Transfer',
      history: 'Istoric',
    },
    deposit: {
      title: 'Depunere',
      history: 'Istoricul depunerilor',
      chain: 'Rețea',
      saveQr: 'Salvează codul QR',
      address: 'Adresă de depunere',
      copyAddress: 'Copiază adresa',
      notice: 'Notificare importantă',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Trimite doar ${asset} în rețeaua ${network}. Fondurile trimise din altă rețea se pierd.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Creditare după ${count.text} ${pluralRo(count, 'confirmare a rețelei', 'confirmări ale rețelei')}, de obicei în aproximativ ${minutes.text} ${pluralRo(minutes, 'minut', 'minute')}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Depunere minimă: ${amount} ${asset}.`,
      waiting: 'Așteptăm transferul',
      incoming: 'Depuneri în curs',
      incomingFirst: 'Detectat în rețea, așteptăm prima confirmare',
      incomingProgress: (done: Count, total: Count) => `Confirmări: ${done.text} din ${total.text}`,
      selectAsset: 'Alege moneda',
      noAssets: 'Depunerea este temporar indisponibilă. Încearcă mai târziu.',
      noNetworks: 'Nu există rețele disponibile pentru această monedă: alege alta.',
      addressFailed: 'Adresa de depunere nu a putut fi obținută',
      noticePermanent: 'Adresa este permanentă: folosește-o pentru toate depunerile viitoare.',
      qrSaved: 'Codul QR a fost salvat',
      qrFailed: 'Codul QR nu a putut fi salvat',
      paymentLink: 'Link pentru portofel',
      paymentLinkHint: 'Deschide-l în portofel: rețeaua și adresa se completează singure.',
      copyLink: 'Copiază linkul',
    },
    withdraw: {
      title: 'Retragere',
      history: 'Istoricul retragerilor',
      asset: 'Moneda retragerii',
      selectAsset: 'Alege moneda',
      network: 'Rețeaua retragerii',
      address: 'Adresă de retragere',
      addressPlaceholder: (network: string) => `Introdu o adresă ${network}`,
      addressPaste: 'Lipește adresa',
      addressFromBook: (label: string): string => `Confirmată în agenda de adrese · ${label}`,
      paste: 'Lipește',
      addressInvalid: (network: string) => `Nu pare o adresă din rețeaua ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `Este o adresă din rețeaua ${detected}, dar este selectată ${selected}`,
      amount: 'Suma retragerii',
      amountPlaceholder: '0.00',
      networkFee: 'Comision de rețea',
      platformFee: (rate: string): string => `Comisionul platformei (${rate}%)`,
      feeFree: 'momentan fără comision',
      feeDynamic: 'la prețul actual al gazului',
      willReceive: 'Vei primi',
      notice: 'Notificare importantă',
      noticeReview: 'Fiecare cerere este verificată înainte de trimitere. Fondurile sunt rezervate imediat.',
      noticeTiming: 'Trimitere în 24 de ore de la aprobare.',
      noticeMinimum: (amount: string, asset: string) =>
        `Retragere minimă: ${amount} ${asset}.`,
      noticeSupport:
        'Verifică singur adresa: un transfer către o adresă străină nu poate fi recuperat. Asistența analizează contestațiile după numărul cererii.',
      insufficient: 'Fonduri insuficiente',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Rămas astăzi: ${remaining} din ${limit} USDT.` : `Fără verificare: până la ${limit} USDT pe zi, au rămas ${remaining}. Fă verificarea ca să ridici limita.`,
      addressNotConfirmed: 'Retrageri doar către adrese din agendă, confirmate prin e-mail',
      limitReached: (verified: boolean): string => (verified ? 'Limita zilnică de retragere a fost epuizată' : 'Limita zilnică fără verificare a fost epuizată: fă verificarea în profil'),
      belowMinimum: (amount: string, asset: string) => `Minimum ${amount} ${asset}`,
      confirmTitle: 'Verifică cererea',
      confirmSubtitle: 'O retragere trimisă nu mai poate fi anulată. Verifică rețeaua și adresa.',
      confirmAction: 'Confirmă retragerea',
      failed: 'Cererea nu a putut fi creată',
      submitted: 'Cerere creată',
      submittedHint: 'După verificare, fondurile pleacă în rețea. Urmărește starea în cererile de retragere.',
      requestId: 'Numărul cererii',
      statusPending: 'În verificare',
      toRequests: 'La cererile de retragere',
      empty: 'Momentan nu există monede disponibile pentru retragere',
      loadFailed: 'Lista monedelor nu a putut fi încărcată',
      routeUnavailable: 'Retragerile pe această rețea nu sunt disponibile acum: alege altă rețea',
      invalidAmount: 'Suma nu este potrivită: verifică precizia și comisionul rețelei',
      edit: 'Corectează cererea',
    },
    withdrawals: {
      title: 'Cereri de retragere',
      toAddress: (address: string): string => `către ${address}`,
      note: 'Cererile respinse sau eșuate returnează rezerva în cont. Deschide o cerere pentru a vedea tranzacția sau motivul.',
      empty: 'Nicio cerere de retragere deocamdată. Fiecare cerere își va arăta aici starea, de la verificare până la confirmarea rețelei.',
      loadError: 'Cererile nu au putut fi încărcate',
      details: 'Cerere de retragere',
      created: 'Creată',
      transaction: 'Tranzacție',
      openExplorer: 'Deschide în explorer',
      reason: 'Motiv',
      status: {
        pending: 'În verificare',
        approved: 'Aprobată',
        rejected: 'Respinsă',
        broadcasting: 'Se trimite',
        sent: 'Trimisă',
        confirmed: 'Confirmată',
        failed: 'Eșuată',
      },
      statusHint: {
        pending: 'Cererea este în verificare. Suma este rezervată în cont.',
        approved: 'Verificată, așteaptă trimiterea în rețea.',
        rejected: 'Cererea a fost respinsă; rezerva a revenit în cont.',
        broadcasting: 'Tranzacția se trimite în rețea.',
        sent: 'Tranzacția este în rețea, așteptăm confirmarea.',
        confirmed: 'Rețeaua a confirmat tranzacția. Fondurile au ajuns.',
        failed: 'Trimiterea a eșuat; rezerva a revenit în cont.',
      },
    },
    transfer: {
      title: 'Transfer',
      from: 'Din',
      fromAvailable: (amount: string): string => `Din · ${amount} disponibil`,
      to: 'În',
      submit: 'Transferă',
      swap: 'Inversează conturile',
      selectFrom: 'Cont sursă',
      selectTo: 'Cont destinație',
      selectAsset: 'Alege moneda',
      after: 'După transfer',
      insufficient: 'Fonduri insuficiente în contul sursă',
      done: 'Transfer efectuat',
      hint: 'Transferurile între conturile tale sunt instantanee și fără comision.',
      empty: 'Nu ai încă ce transfera: alimentează mai întâi contul.',
      failed: 'Transferul nu a putut fi efectuat. Încearcă din nou',
    },
    history: {
      title: 'Istoricul operațiunilor',
      deposit: 'Depunere',
      withdrawal: 'Retragere',
      trade: 'Tranzacție',
      fee: 'Comision',
      transfer: 'Transfer',
      pnl: 'Rezultat',
      empty: 'Nu există încă operațiuni. Aici vor apărea depunerile, retragerile și tranzacțiile.',
      emptyFiltered: 'Nu există operațiuni pentru acest filtru',
      loadError: 'Istoricul nu a putut fi încărcat',
      today: 'Astăzi',
      yesterday: 'Ieri',
      balanceAfter: (amount: string) => `sold ${amount}`,
      onlyAccount: (account: string) => `Doar ${account}`,
      resetAccount: 'Arată toate conturile',
      accountFilter: 'Cont',
      allAccounts: 'Toate conturile',
      withdrawalsLink: 'Starea cererilor de retragere',
      badges: {
        credited: 'Creditat',
        sent: 'Trimis',
        fee: 'Comision',
        reserved: 'Rezervat',
        returned: 'Returnat',
        released: 'Eliberat',
        filled: 'Executat',
        settled: 'Decontat',
        liquidated: 'Lichidat',
        done: 'Finalizat',
        adjusted: 'Ajustat',
        paid: 'Plătit',
      },
      filters: {
        all: 'Toate',
        rewards: 'Bonusuri',
        deposits: 'Depuneri',
        withdrawals: 'Retrageri',
        trades: 'Tranzacții',
        transfers: 'Transferuri',
      },
      types: {
        deposit: 'Depunere',
        withdrawal: 'Retragere',
        withdrawal_fee: 'Comision de retragere',
        withdrawal_platform_fee: 'Comisionul platformei la retragere',
        withdrawal_lock: 'Rezervă pentru retragere',
        withdrawal_release: 'Eliberarea rezervei de retragere',
        order_lock: 'Rezervă pentru ordin limită',
        order_release: 'Eliberarea rezervei ordinului',
        bonus: 'Bonus promoțional',
        trade_buy: 'Cumpărare',
        trade_sell: 'Vânzare',
        trading_fee: 'Comision de tranzacționare',
        realized_pnl: 'Rezultatul tranzacției',
        signal_programmed_profit: 'Profit programat al semnalului',
        funding: 'Finanțare',
        margin_lock: 'Rezervă de marjă',
        margin_release: 'Eliberarea marjei',
        liquidation: 'Lichidare',
        transfer_in: 'Transfer primit',
        transfer_out: 'Transfer trimis',
        copy_allocation: 'Alocat pentru copiere',
        copy_release: 'Revenire din copiere',
        copy_commission: 'Comision de copy trading',
        referral: 'Recompensă de recomandare',
        adjustment: 'Corecție',
      },
    },
  },

  trade: {
    title: 'Trading',
    menu: {
      account: 'Cont',
      trading: 'Trading',
      more: 'Mai multe',
    },
    long: 'Long',
    short: 'Short',
    buy: 'Cumpără',
    sell: 'Vinde',
    marketOrder: 'La piață',
    limitOrder: 'Limită',
    price: 'Preț',
    marketPrice: 'La piață',
    quantity: 'Cantitate',
    leverage: 'Levier',
    takeProfitStopLoss: 'Take-profit / stop-loss',
    positions: 'Poziții',
    orders: 'Ordine',
    history: 'Istoric',
    copyTrading: 'Copiere tranzacții',
    unrealizedPnl: 'P&L nerealizat',
    unrealizedPnlShort: 'P&L',
    margin: 'Marjă',
    entry: 'Intrare',
    size: 'Dimensiune',
    liquidation: 'Lichidare',
    close: 'Închide',
    orderBook: 'Registru de ordine',
    noPositions: 'Plasează un ordin mai sus și poziția va apărea aici.',
    balance: 'Sold',
    perpetual: 'Perpetuu',
    perpShort: 'Perp',
    fundingIn: (time: string) => `în ${time}`,
    position: 'Poziție',
    closeAtMarket: 'Închide la piață',
    keepPosition: 'Păstrează poziția',
    placeBuyOrder: 'Plasează ordinul de cumpărare',
    placeSellOrder: 'Plasează ordinul de vânzare',
    placeLongOrder: 'Plasează ordinul long',
    placeShortOrder: 'Plasează ordinul short',
    isolatedMargin: 'Marjă izolată',
    crossMargin: 'Marjă cross',
    markShort: 'Marcare',
    liqPrice: 'Preț lichid.',
    noPositionsTitle: 'Nu există poziții deschise',
    noOrdersTitle: 'Nu există ordine active',
    noHistoryTitle: 'Nu există încă tranzacții',
    pair: 'Pereche',
    selectPair: 'Alege perechea',
    sideLabel: 'Direcție',
    orderType: 'Tip de ordin',
    sizeShare: 'Parte din disponibil',
    quantityShort: 'Cant.',
    takeProfit: 'Take-profit',
    stopLoss: 'Stop-loss',
    tabsLabel: 'Tranzacțiile mele',
    openChart: 'Grafic',
    openLong: (symbol: string) => `Long ${symbol}`,
    openShort: (symbol: string) => `Short ${symbol}`,
    buyAsset: (symbol: string) => `Cumpără ${symbol}`,
    sellAsset: (symbol: string) => `Vinde ${symbol}`,
    enterQuantity: 'Indică o cantitate',
    insufficient: 'Fonduri insuficiente în cont',
    belowMinNotional: (amount: string, asset: string) =>
      `Volumul minim al ordinului: ${amount} ${asset}`,
    bookEmpty: 'Nu există încă ordine în registru',
    bookUnavailable: 'Registrul de ordine nu este disponibil',
    tradingUnavailable:
      'Serviciul de trading nu răspunde: ordinul nu poate fi trimis acum. Soldurile, depunerile și retragerile funcționează normal.',
    marketsFailed: 'Lista perechilor nu a putut fi încărcată',
    noMarkets: 'Nu există încă perechi: vor apărea pe măsură ce piețele sunt lansate.',
    confirmTitle: 'Verifică ordinul',
    confirmSubtitle: 'Un ordin executat nu poate fi anulat.',
    marginRequired: 'Marjă folosită',
    cost: 'De plătit',
    liquidationEstimate: 'Lichidare estimată',
    submitted: 'Ordin trimis',
    submitFailed: 'Ordinul nu a putut fi trimis',
    futuresSoon: 'Contractele futures sunt temporar indisponibile. Tradingul spot funcționează.',
    marginMode: 'Marjă',
    cross: 'Cross',
    isolated: 'Izolată',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Cross: întregul sold futures susține poziția'
        : 'Izolată: se riscă doar marja acestei poziții',
    reduceOnly: 'Doar reducere',
    futuresAccount: 'Cont futures',
    equity: 'Capital',
    marginUsed: 'În marjă',
    marginRatio: 'Nivel de risc',
    funding: 'Finanțare',
    fundingRate: 'Rată la 8 h',
    nextFunding: 'Următoarea',
    fundingPaid: 'Finanțare',
    protection: 'Protecție',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take-profit și stop-loss',
    protectionSheetSubtitle: 'Motorul va închide poziția la prețul pieței imediat ce prețul atinge nivelul.',
    clearLevel: 'Fără nivel',
    addMargin: 'Adaugă marjă',
    removeMargin: 'Scoate marjă',
    marginDelta: 'Suma marjei',
    marginDeltaHint: 'Poziție izolată: mai multă marjă îndepărtează lichidarea',
    addMarginKyc: 'Adăugarea de marjă se deschide după aprobarea documentelor. Poți deja să scoți marjă, să modifici take-profit și stop-loss sau să închizi poziția.',
    saveProtection: 'Salvează',
    protectionSaved: 'Protecția a fost actualizată',
    protectionFailed: 'Poziția nu a putut fi actualizată',
    protectionInvalid: 'Nivelul este pe partea greșită a prețului de intrare',
    leverageTooHigh: 'Levierul depășește maximul permis pentru această pereche',
    leverageTier: (cap: number, notional: string) => `Pentru un volum de ${notional} USDT, levier de până la ${cap}×`,
    futuresDisabled: 'Ordinele futures nu sunt acceptate temporar',
    liquidated: 'Lichidată',
    restingToast: (symbol: string, price: string) => `Ordinul limită ${symbol} la ${price} a fost plasat și așteaptă prețul`,
    cancelledToast: 'Ordin anulat, fondurile au fost returnate',
    costSell: 'De primit',
    costBasis: 'Cost',
    enterProtection: 'Nivelul de take profit sau stop loss nu este un preț valid',
    leverageLocked: (leverage: number) => `Levierul este fixat la ${leverage}× de poziția deschisă. Închide-o pentru a-l schimba`,
    marginReleased: 'Marjă returnată',
    realizedEstimate: 'Rezultat estimat',
    addsPosition: (quantity: string, asset: string) => `Adaugă ${quantity} ${asset} la poziția deschisă`,
    reducesPosition: (quantity: string, asset: string) => `Reduce poziția deschisă cu ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Închide poziția deschisă de ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Închide poziția de ${closeQuantity} ${asset} și deschide ${openQuantity} ${asset} în direcția opusă`,
    byAccount: 'pe întregul cont',
    cancelledToastPlain: 'Ordin anulat',
    fillBuy: 'Cumpărare',
    fillSell: 'Vânzare',
    marginFloor: (floor: string) => `Marja nu poate scădea sub marja inițială de ${floor} USDT`,
    marginRemovable: (amount: string) => `Se pot retrage până la ${amount} USDT`,
    enterPrice: 'Indică prețul ordinului limită',
    markPrice: 'Preț de marcare',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'Prețul este învechit: fluxul de cotații întârzie. Încearcă peste câteva secunde.',
    marketDisabled: 'Tranzacționarea acestei perechi este suspendată',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `Ordin executat: ${side.toLocaleLowerCase('ro-RO')} ${quantity} ${asset} la ${price}`,
    closedToast: (symbol: string, pnl: string) => `${symbol} închisă, rezultat ${pnl} USDT`,
    positionsFailed: 'Pozițiile nu au putut fi încărcate',
    noOrders: 'Un ordin limită va aștepta aici până la executare.',
    ordersFailed: 'Ordinele nu au putut fi încărcate',
    noHistory: 'Prima tranzacție executată va apărea în această listă.',
    historyFailed: 'Istoricul tranzacțiilor nu a putut fi încărcat',
    cancelOrder: 'Anulează',
    cancelFailed: 'Ordinul nu a putut fi anulat',
    closeFailed: 'Poziția nu a putut fi închisă',
    closeConfirmTitle: 'Închizi poziția?',
    closeConfirmSubtitle:
      'Poziția se va închide la prețul pieței, iar rezultatul curent devine definitiv. Nu se poate anula.',
  },

  chart: {
    title: 'Grafic',
    interval: 'Interval',
    chartType: 'Tip de grafic',
    candles: 'Lumânări',
    line: 'Linie',
    high24h: 'Max 24 h',
    low24h: 'Min 24 h',
    volume24h: 'Volum 24 h',
    empty: 'Graficul va apărea când se vor acumula date pentru această pereche',
    failed: 'Graficul nu a putut fi încărcat',
    unknownPair: 'Această pereche nu există: alege-o din fila „Piețe”',
    toMarkets: 'La piețe',
  },

  security: {
    title: 'Securitate',
    subtitle: 'Al doilea factor protejează autentificarea și retragerile',
    statusOn: 'Activat',
    statusOff: 'Neactivat',
    explain:
      'Codul din aplicația de autentificare este cerut la autentificare și la fiecare cerere de retragere. Fără el, contul este protejat doar de parolă.',
    setup: 'Conectează autentificatorul',
    step1: 'Scanează codul în Google Authenticator, 1Password sau Aegis',
    secretLabel: 'Sau introdu secretul manual',
    step2: 'Introdu codul din aplicație',
    enable: 'Activează',
    enabled: 'Al doilea factor a fost activat',
    disableTitle: 'Dezactivează al doilea factor',
    disableHint: 'Introdu codul curent ca să confirmi că telefonul este la tine',
    disable: 'Dezactivează',
    disabled: 'Al doilea factor a fost dezactivat',
    codeInvalid: 'Codul nu a fost acceptat. Verifică ora telefonului și introdu unul nou',
    failed: 'Setările de securitate nu au putut fi modificate',
    withdrawalCode: 'Cod de confirmare',
    withdrawalCodeHint: 'Retragerea este protejată de al doilea factor: introdu codul din aplicație',
    recoveryTitle: 'Coduri de recuperare',
    recoveryIntro: 'Păstrează aceste coduri într-un loc sigur. Fiecare permite o autentificare sau o retragere dacă telefonul nu este disponibil. Nu vor mai fi afișate.',
    recoveryCopy: 'Copiază codurile',
    recoveryCopied: 'Codurile au fost copiate',
    recoverySaved: 'Am salvat codurile',
    recoveryLeft: (count: number): string => `Coduri de recuperare rămase: ${count}`,
    recoveryRegenerate: 'Coduri de recuperare noi',
    recoveryRegenerateHint: 'Codurile vechi nu vor mai funcționa. Introdu codul din aplicație',
    recoveryRegenerated: 'Au fost emise coduri de recuperare noi',
    codeOrRecovery: 'Cele șase cifre din aplicație sau un cod de recuperare',
  },

  addressBook: {
    title: 'Adrese de retragere',
    hint: 'O adresă nouă devine utilizabilă după confirmarea prin linkul din e-mail. Astfel, o sesiune străină nu poate deturna fondurile către adresa ei.',
    add: 'Adaugă adresă',
    address: 'Adresă',
    label: 'Denumire',
    labelPlaceholder: 'De exemplu, Ledger',
    send: 'Confirmă prin e-mail',
    saved: 'Adrese salvate',
    empty: 'Nicio adresă încă',
    emptyBody: 'Adaugă adresa pe care retragi: după confirmarea din e-mail, o alegi dintr-o atingere în formularul de retragere.',
    statusConfirmed: 'confirmată',
    statusPending: 'așteaptă confirmarea prin e-mail',
    resend: 'Retrimite',
    resendLink: 'Retrimite e-mailul de confirmare',
    remove: 'Șterge adresa',
    removeTitle: 'Ștergi această adresă?',
    removeHint: 'Retragerile către această adresă nu vor fi posibile până nu o adaugi și o confirmi din nou.',
    linkSent: 'E-mail trimis: deschide linkul ca să confirmi adresa',
    alreadyConfirmed: 'Această adresă este deja confirmată',
    invalid: 'Adresa nu corespunde rețelei alese',
    confirmed: (short: string): string => `Adresa ${short} a fost confirmată`,
    confirmFailed: 'Linkul este invalid sau a expirat',
  },

  sessions: {
    title: 'Sesiuni și dispozitive',
    subtitle: 'Unde este autentificat contul tău',
    current: 'Acest dispozitiv',
    started: 'Autentificare',
    lastSeen: 'Activitate',
    end: 'Încheie',
    endOthers: 'Încheie toate celelalte sesiuni',
    ended: 'Sesiune încheiată',
    endedOthers: (count: number): string => `Sesiuni încheiate: ${count}`,
    empty: 'Nu există alte sesiuni active',
    failed: 'Sesiunile nu au putut fi încărcate',
    hint: 'O sesiune încheiată pierde accesul în cincisprezece minute: atât trăiește o cheie de acces deja emisă.',
  },

  profile: {
    title: 'Profil',
    accountId: 'ID cont',
    idCopied: 'ID copiat',
    loadFailed: 'Profilul nu a putut fi încărcat',
    partnerProgram: 'Program de parteneriat',
    partnerProgramHint: 'Echipă, nivel și recompense',
    securityOn: 'Al doilea factor este activat',
    securityOff: 'Activează al doilea factor',

    verification: 'Verificare',
    verificationNone: 'Neefectuată',
    verificationPending: 'În verificare',
    verificationInReview: 'În verificare',
    verificationApproved: 'Finalizată',
    verificationRejected: 'Respinsă',
    verificationResubmit: 'Sunt necesare documente noi',
    verificationCta: 'Începe',
    verificationCtaAgain: 'Trimite din nou',
    verificationPrompt: 'Încarcă pașaportul: durează 2 minute',
    verificationPendingHint: 'Operatorul verifică de obicei documentele în cel mult o zi.',
    verificationRejectedHint: 'Operatorul nu a acceptat documentele. Poți trimite cererea din nou.',
    verificationOpen: 'Deschide cererea',

    withdrawalAddressesHint: 'Agendă de adrese cu confirmare prin e-mail',
    withdrawalAddresses: 'Adrese de retragere',
    addressesSubtitle: 'Adrese către care ai retras deja fonduri',
    addressesEmpty: 'Nu ai retras încă fonduri. Adresa din prima cerere va apărea aici.',
    addressesFailed: 'Adresele nu au putut fi încărcate',
    addressCopied: 'Adresă copiată',

    security: 'Securitate',
    accountAccess: 'E-mail și parolă',
    sessions: 'Sesiuni și dispozitive',
    sessionsHint: 'Unde ești autentificat',
    securitySubtitle: 'Acces la cont',
    securityEmailVerified: 'E-mail confirmat',
    securityEmailUnverified: 'E-mail neconfirmat',
    securityPassword: 'Schimbă parola',
    securityPasswordHint: 'Îți trimitem pe e-mail un link pentru schimbarea parolei',

    notifications: 'Notificări',
    pushOn: 'Push activat',
    pushOff: 'Push dezactivat',
    pushToggle: 'Notificări push',
    pushUnsupported: 'Acest browser nu acceptă notificări push',
    pushDenied: 'Notificările sunt blocate în setările browserului',
    pushFailed: 'Notificările push nu au putut fi activate',
    pushUnavailable: 'Notificările push nu sunt încă configurate pe server',


    theme: 'Temă',
    themeHint: 'Tema neagră este cea a brandului și economisește bateria pe OLED. Cea deschisă e pentru soare puternic. „Ca în sistem” urmează setarea telefonului.',
    themeNames: { system: 'Ca în sistem', dark: 'Întunecată', light: 'Deschisă' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Urmează setarea dispozitivului',
    language: 'Limbă',
    languageHint: 'Limba interfeței. Sumele păstrează un format unic; datele urmează limba.',

    support: 'Asistență',
    supportTitle: 'Contactează asistența',
    supportChoose: 'Alege cum preferi să ne scrii',
    supportVia: 'Telegram sau e-mail',
    supportTelegram: 'Scrie pe Telegram',
    supportEmail: 'Scrie pe e-mail',
    about: 'Despre aplicație',
    version: 'Versiune',

    signOut: 'Ieși',
    signOutTitle: 'Ieși din cont?',
    signOutBody:
      'Soldurile, pozițiile deschise și istoricul semnalelor rămân la locul lor: le regăsești la următoarea autentificare. Celelalte dispozitive rămân conectate.',

    kyc: {
      title: 'Verificare',
      intro:
        'Datele sunt văzute doar de operatorul de verificare. Numărul documentului este stocat criptat și nu este afișat nicăieri în întregime.',
      loadFailed: 'Cererea nu a putut fi încărcată',

      document: 'Document',
      documentType: 'Tip de document',
      typePassport: 'Pașaport',
      typeIdCard: 'Carte de identitate',
      typeDriverLicense: 'Permis de conducere',
      number: 'Numărul documentului',
      numberPlaceholder: '4510 123456',
      numberHint: 'Seria și numărul, ca în document',
      country: 'Țara emitentă',
      countryPlaceholder: 'Alege țara',
      issueDate: 'Data eliberării',
      expiryDate: 'Valabil până la',

      personal: 'Titularul documentului',
      lastName: 'Nume',
      firstName: 'Prenume',
      dateOfBirth: 'Data nașterii',

      documents: 'Fotografia documentului',
      docMain: 'Pagina principală',
      docMainHint: 'Pagina cu fotografia și numărul',
      docFront: 'Fața documentului',
      docFrontHint: 'Partea cu fotografia și numărul',
      choose: 'Alege fișierul',
      replace: 'Înlocuiește',
      remove: 'Elimină',
      fileHint: (size: string) => `JPG, PNG sau PDF, până la ${size} MB`,
      megabytes: 'MB',

      submit: 'Trimite la verificare',
      submitFailed: 'Cererea nu a putut fi trimisă. Verifică datele și încearcă din nou',
      uploadFailed:
        'Datele au fost trimise, dar documentele nu au putut fi încărcate. Încearcă să le atașezi din nou.',
      uploadRetry: 'Încarcă documentele',
      submitted: 'Cerere trimisă',
      submittedHint: 'Un operator va verifica documentele și va actualiza starea.',

      review: 'Cerere în verificare',
      reviewHint: 'Cât timp durează verificarea, datele nu pot fi modificate.',
      approved: 'Verificare finalizată',
      approvedHint: 'Operatorul a confirmat documentele.',
      rejected: 'Cerere respinsă',
      rejectedHint: 'Corectează datele și trimite cererea din nou.',
      resubmit: 'Sunt necesare documente noi',
      resubmitHint: 'Încarcă un document valabil și trimite cererea din nou.',
      rejectionReason: 'Motiv',
      submittedAt: (date: string) => `Trimisă la ${date}`,
      attached: 'Documente atașate',
      attachedEmpty: 'Nu este atașat niciun fișier la cerere',
      startNew: 'Trimite din nou',
      step: (current: number, total: number): string => `Pasul ${current} din ${total}`,

      gate: {

        title: 'Verifică-ți identitatea ca să folosești fondurile',

        body: 'Poți depune chiar acum. Tradingul, semnalele, transferurile și retragerile se deschid după aprobarea documentelor, de obicei în cel mult o zi.',

        pending: 'Documentele sunt în verificare: accesul se deschide după aprobare, de obicei în cel mult o zi.',

        action: 'Fă verificarea',

      },

      kinds: {
        passport_main: 'Pagina principală',
        passport_registration: 'Pagina cu domiciliul',
        selfie_with_document: 'Selfie cu documentul',
        proof_of_address: 'Dovada adresei',
      },

      errors: {
        required: 'Completează câmpul',
        number: 'Doar litere latine, cifre, spațiu și cratimă',
        name: 'Doar litere, spațiu, apostrof și cratimă',
        country: 'Alege țara',
        date: 'Indică data',
        issueFuture: 'Data eliberării nu poate fi în viitor',
        expiryPast: 'Documentul a expirat',
        expiryBeforeIssue: 'Data expirării trebuie să fie după data eliberării',
        age: 'Verificarea este disponibilă de la 18 ani',
        birthAfterIssue: 'Data nașterii trebuie să fie înaintea datei eliberării',
        fileType: 'Este acceptat un fișier JPG, PNG sau PDF',
        fileSize: (size: string) => `Fișierul depășește ${size} MB`,
        documentsRequired: 'Atașează o fotografie sau o scanare a documentului',
        fileMissing: 'Adaugă acest fișier',
      },
    },
  },

  countries: {
    search: 'Numele sau codul țării',
    popular: 'Frecvente',
    all: 'Toate țările',
    nothing: 'Niciun rezultat. Încearcă numele în engleză sau un cod, de exemplu DE',
    found: (count: number): string => `Țări găsite: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Bonusul promoțional de ${amount} USDT a fost deblocat și creditat în spot`,
    promoExpired: 'Bonusul promoțional a expirat: condițiile nu au fost îndeplinite la timp',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Invitatul tău ${name} s-a calificat: +${signals.text} ${pluralRo(signals, 'semnal bonus', 'semnale bonus')}`
        : `Invitatul tău ${name} s-a calificat`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} ${pluralRo(signals, 'semnal bonus', 'semnale bonus')}` : null,
        promo === null ? null : `un bonus promoțional de ${promo} USDT blocat până la îndeplinirea condiției`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'Calificarea ta prin invitație este completă' : `Calificare completă: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Calificare revocată: a fost cerută o retragere în perioada de reținere. Semnalele bonus și bonusul promoțional sunt retrase'
          : 'Calificare revocată: semnalele bonus și bonusul promoțional sunt retrase'
        : `Calificarea invitatului tău ${name} a fost revocată: semnalele bonus primite datorită lui sunt retrase`,
    referralRewardPaid: (amount: string): string => `${amount} USDT plătiți prin programul de parteneriat`,
    referralRewardRejected: (amount: string): string => `O recompensă de ${amount} USDT a fost respinsă de operator`,
    partnerLevelReached: (level: string, amount: string): string =>
      `Nivelul ${level} atins: un bonus de ${amount} USDT așteaptă plata`,
    title: 'Notificări',
    settingsTitle: 'Setări notificări',
    settingsHint: 'O categorie dezactivată rămâne în istoric, dar nu deranjează: fără insignă și fără push. Push funcționează când este activat în profil.',
    channelInApp: 'În aplicație',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'Ce trimite botul în chat. O categorie oprită aici nu ajunge în Telegram, dar rămâne în aplicație.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram conectat' : `Telegram conectat: ${name}`),
    telegramLinkedHint: 'Dacă nu ai fost tu, atinge pentru a-l deconecta.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram deconectat' : `Telegram deconectat: ${name}`),
    telegramUnlinkedHint: 'Acest chat a fost conectat la alt cont.',
    categories: { deposit: 'Depuneri', withdrawal: 'Retrageri', copy: 'Copy trading', trade: 'Tranzacții și poziții', signal: 'Semnale', promo: 'Bonusuri promoționale', referral: 'Parteneriat', kyc: 'Verificare', system: 'Sistem' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Marchează toate ca citite',
    markAllFailed: 'Notificările nu au putut fi marcate ca citite',
    loadFailed: 'Notificările nu au putut fi încărcate',
    empty:
      'Nu există încă notificări. Aici vor apărea creditările, răspunsurile la cererile de retragere, semnalele și rezultatele tranzacțiilor.',
    unread: 'Necitită',
    today: 'Astăzi',
    yesterday: 'Ieri',
    depositCredited: (amount: string, asset: string) =>
      `Creditat ${amount} ${asset}`,
    withdrawalApproved: 'Retragere aprobată',
    withdrawalRejected: 'Retragere respinsă',
    withdrawalSent: 'Retragere trimisă în rețea',
    marginCall: 'Apel în marjă',
    positionLiquidated: 'Poziție lichidată',
    copyOpened: (master: string) => `${master} a deschis o poziție`,
    copyClosed: (master: string) => `${master} a închis o poziție`,
    copyStopLoss: 'Stop-loss-ul abonamentului s-a declanșat: pozițiile au fost închise, fondurile au revenit în contul spot',
    copyMasterStopped: 'Traderul a fost oprit: copierea s-a încheiat, fondurile au revenit în contul spot',
    copyReleased: (amount: string) => `Returnat în spot: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Comision către trader: ${commission} USDT din profitul de ${profit} USDT` : `Comision către ${master}: ${commission} USDT din profitul de ${profit} USDT`,
    signalLive: (title: string, until: string) => `Semnalul „${title}” este deschis până la ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Poziția ${symbol} a fost lichidată la ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: take-profit declanșat la ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: stop-loss declanșat la ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Ordinul limită ${symbol} a fost executat: ${quantity} la ${price}`,
    orderRejected: (symbol: string) => `Ordinul limită ${symbol} nu a fost executat: motorul a respins tranzacția`,
    orderRejectedFunds: (symbol: string) => `Ordinul limită ${symbol} nu a fost executat: marjă insuficientă pe contul futures`,
    orderRejectedLeverage: (symbol: string) => `Ordinul limită ${symbol} nu a fost executat: levierul depășește limita pentru această mărime`,
    kycApproved: 'Verificare finalizată',
    kycRejected: 'Verificare respinsă',
    kycResubmit: 'Verificarea necesită documente noi',
    kycExpiring: (date: string) => `Documentul expiră la ${date}: reînnoiește verificarea`,
    kycExpired: 'Documentul a expirat: încarcă un document valabil',
    signalClosed: (title: string) => `Semnal închis: ${title}`,
    systemAlert: (check: string) => `Incident: ${check}`,
    systemRecovered: (check: string) => `Restabilit: ${check}`,
    heads: {
      signalLive: 'Semnal deschis',
      signalClosed: 'Semnal închis',
      orderFilled: 'Ordin limită executat',
      orderRejected: 'Ordin limită neexecutat',
      takeProfit: 'Take-profit declanșat',
      stopLoss: 'Stop-loss declanșat',
      promoUnlocked: 'Bonus promoțional deblocat',
      promoExpired: 'Bonus promoțional expirat',
      referralQualified: 'Invitat calificat',
      referralWelcome: 'Calificare completă',
      referralRevoked: 'Calificare revocată',
      rewardPaid: 'Recompensă plătită',
      rewardRejected: 'Recompensă respinsă',
      partnerLevelReached: 'Nivel de partener nou',
      kycExpiring: 'Documentul expiră curând',
      kycExpired: 'Document expirat',
      systemAlert: 'Incident',
      systemRecovered: 'Restabilit',
    },
    fallback: {
      deposit: 'Depunere creditată',
      withdrawal: 'Actualizare a cererii de retragere',
      trade: 'Eveniment la poziție',
      copy: 'Eveniment de copy trading',
      referral: 'Eveniment al programului de parteneriat',
      system: 'Notificare de sistem',
    },
  },

  referrals: {
    title: 'Program de parteneriat',
    rewardKind: {
      fee_rebate: 'Parte din comision',
      signal_grant: 'Semnale suplimentare',
      promotional_credit: 'Bonus promoțional',
      deposit_bonus: 'Cotă din depozit',
      welcome_bonus: 'Cotă de bun venit',
      level_bonus: 'Bonus de nivel',
    },
    rewardStatus: {
      pending_review: 'În verificare',
      approved: 'Aprobată',
      paid: 'Plătită',
      rejected: 'Respinsă',
      reversed: 'Anulată',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Blocat',
      unlocked: 'Creditat',
      expired: 'Expirat',
      revoked: 'Revocat',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Membru',
      bronze: 'Bronz',
      silver: 'Argint',
      gold: 'Aur',
      platinum: 'Platină',
      ambassador: 'Ambasador',
    } as Record<string, string>,
    signalUnit: 'semnale',
    membersFrom: (count: Count): string => `de la ${count.text} ${pluralRo(count, 'membru', 'membri')}`,
    baseLevel: 'nivel de start',
    depositFrom: (amount: string): string => `depozit de la ${amount}`,
    noWeeklySignals: 'fără semnale',
    perWeek: (count: Count): string => `${count.text} ${pluralRo(count, 'semnal', 'semnale')}/săpt.`,
    current: 'actual',
    copyFailed: 'Nu s-a putut copia',
    inviteText: 'Alătură-te 19CRT cu codul meu',
    linkCopied: 'Link copiat',
    codeCopied: 'Cod copiat',
    loadError: 'Programul de parteneriat nu a putut fi încărcat',
    loadErrorHint: 'Verifică conexiunea și încearcă din nou',
    retry: 'Reîncearcă',
    disabled:
      'Programul de parteneriat este suspendat temporar. Statisticile și recompensele tale sunt păstrate.',
    yourCode: 'Codul tău',
    codeHint: 'Invită direct: condițiile sunt vizibile înainte de înregistrare',
    openQr: 'Deschide codul QR de invitație',
    qrCode: 'Cod QR',
    share: 'Distribuie',
    copy: 'Copiază',
    yourLevel: 'Nivelul tău',
    participant: 'Membru',
    allLevels: 'Toate nivelurile',
    progressLabel: 'Progres către nivelul următor',
    qualified: (count: Count): string => pluralRo(count, 'calificat', 'calificați'),
    nextLevel: (remaining: Count, level: string): string => `încă ${remaining.text} până la nivelul ${level}`,
    maxLevel: 'nivel maxim',
    invited: 'Invitați',
    confirmed: 'Confirmați',
    paidUsdt: 'Plătit, USDT',
    signalsReceived: 'Semnale primite',
    transparentTitle: 'Recompensă transparentă',
    transparentHint:
      'Recompensa se calculează doar din comisionul de tranzacționare reținut efectiv. Depunerea celui invitat nu este redistribuită.',
    rewards: 'Recompense',
    rewardHistory: 'Istoricul recompenselor',
    underReview: 'În verificare',
    emptyRewardsTitle: 'Încă nu există recompense',
    emptyRewards: 'Recompensele vor apărea după prima activitate calificată a echipei tale.',
    levelsRules: 'Niveluri și reguli',
    levelsRulesHint: 'Echipă, parte din comision și semnale',
    inviteTitle: 'Invitație la 19CRT',
    inviteSubtitle: 'Codul se asociază la înregistrare',
    copyLink: 'Copiază linkul',
    levelsTitle: 'Niveluri de parteneriat',
    levelsSubtitle: 'Se iau în calcul doar membrii direcți calificați',
    loadLevelsError: 'Nivelurile nu au putut fi încărcate',
    levelTeam: 'Nivel și echipă',
    feeSignals: 'Comision · semnale',
    depositTiersTitle: 'Procente după depozit',
    depositTiersHint: 'Cu cât primul depozit e mai mare, cu atât cota e mai mare pentru ambii',
    tierDeposit: 'Depozit',
    tierReferrer: 'Ție',
    tierWelcome: 'Noul membru',
    tierFrom: (amount: string): string => `de la ${amount}`,
    levelBonus: 'Bonus de nivel',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Regulile programului',
      qualification: (amount: string, asset: string): string =>
        `Calificare: o primă depunere creditată de cel puțin ${amount} ${asset} într-o singură tranzacție.`,
      qualificationKyc: 'Este necesară verificarea identității.',
      welcome: (signals: Count, days: Count): string =>
        `Noul membru primește ${signals.text} ${pluralRo(signals, 'semnal bonus valabil', 'semnale bonus valabile')} ${days.text} ${pluralRo(days, 'zi', 'zile')}.`,
      referrer: (signals: Count, days: Count): string =>
        `Partenerul primește ${signals.text} ${pluralRo(signals, 'semnal bonus valabil', 'semnale bonus valabile')} ${days.text} ${pluralRo(days, 'zi', 'zile')} pentru fiecare invitat calificat.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Un bonus promoțional de ${amount} USDT este blocat și devine bani în spot când rulajul tranzacțiilor noului membru atinge ${required} USDT în ${days.text} ${pluralRo(days, 'zi', 'zile')}; altfel expiră.`,
      hold: (days: Count): string =>
        `Orice cerere de retragere în ${days.text} ${pluralRo(days, 'zi', 'zile')} de la calificare o anulează împreună cu semnalele bonus și bonusul promoțional.`,
      feeShare:
        'Cota de comision a nivelului se calculează doar din comisioanele de tranzacționare încasate efectiv de la invitat. Recompensele în bani sunt verificate de operator și plătite separat — nu se creditează niciodată în sold.',
      secondLevel: (rate: string): string => `Partenerul partenerului primește ${rate} din același comision.`,
      depositTiers:
        'Cota pentru cel care invită și pentru noul membru depinde de valoarea primului depozit — vezi tabelul din această fereastră.',
      balanceShare: (percent: string): string =>
        `Depozitul trebuie să fie de cel puțin ${percent} % din soldul celui care invită în momentul creditării.`,
      topup: (days: Count): string =>
        `Un depozit suplimentar în ${days.text} ${pluralRo(days, 'zi', 'zile')} de la primul se adaugă la acesta, perechea este reevaluată conform tabelului, iar diferența se plătește o singură dată.`,
      premiumSignal: (count: string): string =>
        `Accesul permanent la semnalul premium se deschide de la ${count} invitați calificați.`,
      antiabuse:
        'Un dispozitiv sau o adresă comune cu partenerul sau cu echipa lui trimit calificarea la verificare de către operator.',
      paused: 'Programul este suspendat de operator: nu se creează calificări sau recompense noi.',
    },
    disclaimer:
      'Recompensele de partener nu sunt un venit garantat. Înregistrarea, depunerile și rulajul de tranzacționare nu creează singure o plată în bani.',
    team: 'Echipă',
    teamStatus: {
      pending: 'așteaptă o depunere',
      flagged: 'în verificare la operator',
      qualified: 'calificat',
      revoked: 'calificare revocată',
    },
    joined: (date: string): string => `din ${date}`,
    showAll: (count: Count): string => `Arată toți (${count.text})`,
    showLess: 'Restrânge',
    promoTitle: 'Bonus promoțional',
    promoLocked: (amount: string): string => `${amount} USDT blocați`,
    promoUnlocked: (amount: string): string => `${amount} USDT creditați în spot`,
    promoExpired: 'Termenul a expirat, bonusul s-a pierdut',
    promoRevoked: 'Bonus revocat',
    promoCondition: (traded: string, required: string): string => `Rulaj ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `până la ${date}`,
    promoHint: 'Bonusul devine bani când rulajul tranzacțiilor atinge condiția înainte de termen.',
  },

  signals: {
    title: 'Semnale',
    tabs: { today: 'Astăzi', history: 'Istoric' },
    tabsLabel: 'Secțiunile semnalelor',
    access: { public: 'Public', base: 'Bază', premium: 'Premium', bonus: 'Bonus', agent: 'Partener' },
    status: { scheduled: 'Programat', available: 'Disponibil', executed: 'Executat', expired: 'Expirat', cancelled: 'Anulat' },
    side: { buy: 'Cumpărare', sell: 'Vânzare' },
    direction: { buy: 'Long · cumpărare', sell: 'Short · vânzare' },
    positionSide: { buy: 'Long', sell: 'Short' },
    localTime: 'ora locală',
    localClock: 'Ora locală',
    timeZone: (offset: string) => `Fus orar ${offset}`,
    reason: {
      unavailable: 'Acest semnal nu este disponibil acum',
      already: 'Ai executat deja acest semnal',
      expired: 'Fereastra de executare a expirat',
      notReleased: 'Acest semnal nu s-a deschis încă',
      cancelled: 'Acest semnal a fost anulat',
      balance: 'Fonduri insuficiente în contul spot',
      balanceFutures: 'USDT insuficienți în conturile spot și futures la un loc',
      positionConflict: 'Ai o poziție deschisă pe această piață în direcția opusă: închide-o ca să execuți semnalul',
      price: 'Prețul acestei piețe nu este disponibil acum: încearcă din nou în câteva secunde',
      access: 'Acest semnal necesită alt nivel de acces',
      kyc: 'Finalizează mai întâi verificarea: după aprobarea documentelor vei putea executa semnalul',
      minimum: 'Acest semnal ia o cotă din soldul tău total, iar ea este sub minimul lui. Alimentează soldul ca să-l execuți',
      maximum: 'Suma depășește maximul acestui semnal',
      belowMinimum: (amount: string) => `Acest semnal ia o cotă din soldul tău total, iar ea este sub minimul lui de ${amount}\u00A0USDT. Alimentează soldul ca să-l execuți`,
      aboveMaximum: (amount: string) => `Suma depășește maximul acestui semnal: cel mult ${amount} USDT`,
      notFound: 'Nu există niciun semnal cu acest cod',
      generic: 'Acțiunea nu a putut fi finalizată. Verifică condițiile și încearcă din nou',
      network: 'Serverul nu a putut fi contactat. Verifică conexiunea și încearcă din nou',
    },
    enterCode: 'Introdu codul',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Introdu codul din canal: înainte de confirmare vei vedea direcția, suma și condițiile', executed: 'Rezultatul și parametrii reali sunt în fila Istoric', scheduled: (time: string) => `Codul va apărea în canal la ${time}` },
    executing: 'Se execută',
    notExecuted: 'Neexecutat',
    executedLabel: 'Executat',
    loadSignalsError: 'Semnalele nu au putut fi încărcate',
    noSignalsToday: 'Niciun semnal astăzi',
    loadErrorHint: 'Verifică conexiunea și încearcă din nou',
    retry: 'Reîncearcă',
    signalAvailable: 'Semnal disponibil',
    nextSignal: 'Următorul semnal',
    windowOpenUntil: (time: string) => `Fereastră deschisă până la ${time}`,
    opensAt: (time: string) => `Se deschide la ${time}`,
    execute: 'Execută',
    doneToday: 'Asta e tot pentru azi',
    newBySchedule: 'Semnalele noi vor apărea conform programului',
    codeOverline: 'Cod de semnal',
    checkTerms: 'Verifică condițiile',
    codeHint: 'Fiecare cod este publicat în canalul nostru de Telegram: introdu-l imediat ce îl vezi.',
    openChannel: 'Deschide canalul',
    channelLink: 'canalul nostru de Telegram',
    panelTitle: 'Codul tranzacției',
    dealsTitle: 'Tranzacții prin cod',
    dealsEmpty: 'Încă nu există tranzacții prin cod',
    dealsEmptyHint: 'Introdu un cod din canal: tranzacția și rezultatul ei vor apărea aici',
    codePlaceholder: 'De exemplu, 1QF9ET7RQ',
    codeRequired: 'Introdu codul din canal',
    codeLabel: 'Cod de semnal',
    check: 'Verifică',
    schedule: 'Program',
    todaySignals: 'Semnalele de azi',
    noSignals: 'Nu există semnale acum. Codurile noi apar conform programului în canalul nostru de Telegram.',
    riskDisclosure:
      'Un semnal este o idee de tranzacționare, nu un rezultat garantat. Înainte de confirmare vei vedea direcția, suma, levierul, comisionul și condițiile de închidere.',
    loadHistoryError: 'Istoricul semnalelor nu a putut fi încărcat',
    historyEmpty: 'Istoricul este gol',
    historyEmptyHint: 'Aici vor apărea semnalele executate și parametrii lor reali.',
    toSignals: 'La semnale',
    allExecutions: 'Toate executările',
    signalAccepted: 'Semnal acceptat',
    confirmation: 'Confirmarea semnalului',
    sentToTrading: 'Ordinul a fost transmis sistemului de trading',
    checkBeforeExecute: 'Verifică condițiile înainte de executare',
    acceptedHint: 'O nouă apăsare nu va crea un al doilea ordin.',
    ready: 'Gata',
    account: 'Cont',
    spot: 'Spot',
    futures: 'Futures',
    perpetualMarket: 'futures',
    validUntil: 'Valabilitate',
    signalName: 'Semnal',
    until: (time: string) => `până la ${time}`,
    timeLeft: (countdown: string) => `încă ${countdown}`,
    priceGuide: 'Preț orientativ',
    entryGuide: 'Intrare orientativă',
    marketPrice: 'Piață',
    stopLoss: 'Stop-loss',
    takeProfit: 'Take-profit',
    autoClose: 'Se închide singură',
    inDuration: (duration: string) => `în ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0${pluralRo(days, 'zi', 'zile').replace(' ', '\u00A0')}` : null, hours > 0 ? `${hours}\u00A0h` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0min` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Deja executat',
    executionUnavailable: 'Executare indisponibilă',
    executionAmount: 'Suma executării',
    marginAmount: 'Marjă',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} din soldul total · ${total}\u00A0USDT`,
    estimatedFee: 'Comision estimat',
    totalDebit: 'Total de reținut',
    positionSize: 'Mărimea poziției',
    willSell: 'De vândut',
    fundingNote: (amount: string) =>
      `Pentru această tranzacție, ${amount} USDT vor trece din contul spot în contul futures. La închiderea poziției, această sumă, ajustată cu rezultatul, revine în contul spot.`,
    executionRisk:
      'Un semnal este o idee de tranzacționare, nu un rezultat garantat. Prețul de executare poate diferi de cel estimat, iar tranzacția poate aduce pierderi. Confirmă doar un risc pe care îl înțelegi.',
    executionRiskFutures:
      'Un semnal este o idee de tranzacționare, nu un rezultat garantat. Prețul de intrare poate diferi de cel estimat, iar levierul amplifică atât câștigul, cât și pierderea, până la lichidarea poziției. Confirmă doar un risc pe care îl înțelegi.',
    retryExecution: 'Reia executarea',
    outcomeLabel: 'rezultat, USDT',
    outcomeOpen: 'în lucru',
    outcome: { take_profit: 'închis la țintă', stop_loss: 'închis la stop', time: 'închis la termen', manual: 'închis manual', liquidation: 'închis prin lichidare' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'La țintă', stop_loss: 'La stop', time: 'La termen', manual: 'Manual', liquidation: 'Lichidat' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'În lucru',
    executeFor: 'Execută până la',
    openLong: (amount: string) => `Deschide long · ${amount} USDT`,
    openShort: (amount: string) => `Deschide short · ${amount} USDT`,
    submittedToast: 'Semnalul a fost trimis la executare',
    filledOf: (requested: string, filled: string) =>
      `Solicitat ${requested} USDT, executat ${filled} USDT: cantitatea a fost rotunjită la pasul lotului.`,
    marginRounded: (requested: string) => `Solicitat ${requested} USDT: cantitatea a fost rotunjită la pasul lotului.`,
    longOpened: (margin: string, leverage: number) => `Long deschis: marjă ${margin} USDT, levier ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `Short deschis: marjă ${margin} USDT, levier ${leverage}×.`,
    fundedFromSpot: (amount: string) => `${amount} USDT transferați din contul spot.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'la take-profit' : null, stopLoss ? 'la stop-loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Se va închide singură în ${duration}`
      const exits = duration === null ? levels : [...levels, `în ${duration}`]
      const last = exits[exits.length - 1] ?? ''
      return `Se va închide ${exits.length === 1 ? last : `${exits.slice(0, -1).join(', ')} sau ${last}`}`
    },
    openPosition: 'Mergi la poziție',
    returnedToSpot: (amount: string) => `Returnat în spot: ${amount} USDT`,
    positionAccepted: 'Poziția este deschisă în contul futures',
  },

  notFound: {
    title: 'Pagina nu a fost găsită',
    message: 'Această pagină nu există: linkul este învechit sau adresa conține o greșeală.',
    action: 'Acasă',
  },
}
